import { z } from "../zod.js";
import { Uuid } from "../identity/admin-schemas.js";
import {
  arrayEmptyIsAbsent,
  CalibrationCaptureShape,
  ProductionNonZeroSignedQty,
  ProductionPositiveQty,
  ProductionQty,
  ProductionSignedQty,
  ProductionUom,
  ScrapReasonCode,
  ShiftCode,
  ShiftLocaleName,
  StationQueueResponse,
  WoOutputKind,
} from "./schemas.js";

// Module 04 — THE floor sync contract (spec 04 §5 row 13 + §5.1), slice
// 04-B-2. This file is the wire truth for the device outbox: §5.1 says "zod
// schemas live in packages/contracts/production/floor-events.ts; this table is
// the wire truth, so divergent client guesses cannot brick offline devices."
//
// ALL ELEVEN §5.1 payload schemas ship HERE, NOW — they are the contract —
// although the apply pipeline APPLIES only the kinds in
// {@link FLOOR_EVENT_KINDS_APPLIED} (six from 04-B-2; `downtime_started` +
// `downtime_ended` joined with 04-C-1's appliers; `calibration_recorded` with
// 04-C-2's, 2026-08-05). The kinds whose
// projections await their 04-C sub-step ({@link FLOOR_EVENT_KINDS_DEFERRED})
// are VALID on the wire and answer `conflicted` with the named-owner reason
// `unsupported_event_kind` — never a silent enum strip, never a 400 that
// poisons the queue (D-033).
//
// RULES, verbatim from §5.1 and §3.4:
//
//  * Devices send RAW scan strings (`scan_code`) for lots/stock units/serials
//    — never client-resolved ids from cache. The server resolves scan → id
//    and answers `unknown_reference` when it cannot.
//  * Server-resolved fields are NEVER on the wire: `shift_instance_id`,
//    `produced_shift_instance_id`, `unit_cost_minor`, all rate snapshots,
//    `facility_id`. (A payload carrying them is not an error — zod objects
//    here strip unknown keys — but the server never reads them.)
//  * `worker_id` and `work_center_id` ride the batch ENVELOPE (endpoint 13),
//    never the payload; `work_center_id` comes from the station QR scan
//    (`/q/wc/{work_center_id}`, module 02's kit).
//  * Quantities are DECIMAL STRINGS mirroring numeric(14,3) — never JSON
//    numbers (D-041). Money never rides the floor wire at all.
//  * `client_event_id` is the device-minted uuid v7 stamped AT TAP and sent
//    unchanged on every attempt; UNIQUE (tenant_id, client_event_id) is THE
//    ONLY dedupe (§3.4 — a second qty report is a second physical fact, so
//    the client's dedupeKeyOf returns undefined for every production kind).
//  * `occurred_at` is stamped at TAP from the device clock and preserved
//    VERBATIM server-side; attribution uses the §9.5 clamp, never this value
//    raw and never `recorded_at` (§9.6).
//
// VALIDATION LAYERING, stated because it is load-bearing (§3.4 "validation
// failures do NOT poison the queue"): the batch ENVELOPE
// ({@link FloorEventsBatchRequest}) validates STRICTLY at the HTTP boundary —
// a malformed envelope (missing client_event_id, unknown event_type) is a
// broken client and 400s the batch honestly. Each event's PAYLOAD validates
// PER-KIND inside the apply pipeline ({@link floorEventPayloadSchemas}) — a
// payload failing its schema is floor truth to preserve, so it lands as a
// `rejection` conflict (`unknown_reference` for unresolvable scans,
// `invalid_transition` otherwise, §5.1) with the full payload persisted, and
// the batch answers 200 with that event marked `conflicted`.

const Timestamp = z.string().openapi({ format: "date-time" });

/** The eleven §5.1 event kinds — the closed wire vocabulary (§2.3 CHECK). */
export const FLOOR_EVENT_TYPES = [
  "op_started",
  "op_paused",
  "op_resumed",
  "op_completed",
  "qty_reported",
  "material_issued",
  "downtime_started",
  "downtime_ended",
  "calibration_recorded",
  "serial_lot_captured",
  "defect_handoff_flagged",
] as const;

export const FloorEventType = z.enum(FLOOR_EVENT_TYPES);
export type FloorEventTypeValue = (typeof FLOOR_EVENT_TYPES)[number];

/**
 * §5 e10's `event_type[]` QUERY FILTER, enforced at the controller with the
 * same `ZodPipe` the §9.18 uuid/date filters run (extended to this enum
 * 2026-08-08). The OpenAPI document has declared `event_type` as an array of
 * the eleven kinds since the route shipped, but the runtime accepted any
 * string and answered 200-empty — the contract and the wire disagreeing about
 * what a legal filter is, the exact class §9.18 closed. Rules: absent/empty =
 * no filter (the house `emptyToUndefined` rule, baked in); a single bare value
 * is the one-element array (the qs shape Express hands a controller); every
 * member must be a §5.1 kind or the request answers the documented 400
 * `VALIDATION_FAILED`. Same mechanism as `WorkOrderStatusQueryFilter` /
 * `DowntimeReasonKindQueryFilter` (schemas.ts) — one preprocess, three enums.
 */
export const FloorEventTypeQueryFilter = z.preprocess(
  arrayEmptyIsAbsent,
  z.array(FloorEventType).optional(),
);

/** The kinds the apply pipeline APPLIES: 04-B-2's six, the downtime pair
 * 04-C-1 shipped, `calibration_recorded` (04-C-2, 2026-08-05),
 * `serial_lot_captured` (04-C-3, 2026-08-06) and — as of 04-C-5 (2026-08-07) —
 * `defect_handoff_flagged`, each by deleting its kind from the deferred map
 * below. THE LIST IS NOW ALL ELEVEN: every §5.1 kind has an applier, and
 * {@link FLOOR_EVENT_KINDS_DEFERRED} is empty. */
export const FLOOR_EVENT_KINDS_APPLIED = [
  "op_started",
  "op_paused",
  "op_resumed",
  "op_completed",
  "qty_reported",
  "material_issued",
  "downtime_started",
  "downtime_ended",
  "calibration_recorded",
  "serial_lot_captured",
  "defect_handoff_flagged",
] as const satisfies readonly FloorEventTypeValue[];

/**
 * The kinds whose appliers still await their 04-C sub-step, each mapped to its
 * NAMED OWNER (D-033 — never a silent enum strip). Until that sub-step lands,
 * an event of one of these kinds answers `conflicted` with reason
 * `unsupported_event_kind` and is persisted whole into `floor_sync_conflicts`
 * — floor truth is preserved for the office, never dropped.
 *
 * THE MAP IS THE ROUTER: a kind present here never reaches its applier, so a
 * sub-step SHIPS its appliers by DELETING its kinds from this map in the same
 * PR (the 04-C brief's ship gate). 04-C-1 deleted `downtime_started` and
 * `downtime_ended`; 04-C-2 deleted `calibration_recorded` (2026-08-05 —
 * `calibration_records` exists and the applier is live; the 04-B pins that
 * drove this kind as the refusal probe inverted into the calibration suites,
 * as those pins' own comments predicted); 04-C-3 deleted `serial_lot_captured`
 * (2026-08-06 — `operation_serial_lot_captures` exists, migration 0027, and
 * the 04-B pins that used THIS kind as their refusal probe inverted the same
 * way, labelled as the predicted inversion rather than deleted); 04-C-5 deleted
 * the last one, `defect_handoff_flagged` (2026-08-07 — `defect_handoffs`
 * exists, migration 0029, and the §3.3 step-10 attribution walk runs).
 *
 * THE MAP IS NOW EMPTY, AND IT STAYS. Deleting the constant would delete the
 * router: `unsupported_event_kind` is a §2.17 reason value and a migration-0022
 * CHECK member, and a twelfth §5.1 kind added by a later module must land here
 * — refused with its owner named — rather than falling through to the
 * pipeline's fail-closed default, which answers `invalid_transition` and blames
 * the operator for a slice that has not shipped. An empty map is the honest
 * state of a module whose eleven kinds all apply; a deleted map is a mechanism
 * the next author has to rediscover.
 */
export const FLOOR_EVENT_KINDS_DEFERRED = {} as const satisfies Partial<
  Record<FloorEventTypeValue, string>
>;

/**
 * `floor_sync_conflicts.reason` (§2.17) — the nine spec values plus
 * `unsupported_event_kind`, the named-owner refusal for the
 * {@link FLOOR_EVENT_KINDS_DEFERRED} kinds (D-033; the §2.17 reason list
 * gains the value in this slice's spec amendment set).
 */
export const FloorConflictReason = z.enum([
  "invalid_transition",
  "qty_exceeds_remaining",
  "op_already_completed",
  "wo_cancelled",
  "stock_insufficient",
  "unknown_reference",
  "stale_snapshot",
  "qc_hold",
  "worker_not_authenticated",
  "unsupported_event_kind",
]);

/**
 * The one reason e13 answers `conflicted` with and never persists (D-340
 * ruling 3). A `client_event_id` already stored for ANOTHER worker,
 * as an applied event or as a refusal, is answered `client_event_id_reuse`
 * with no stored reason and no server note, and nothing is written. It is not
 * a {@link FloorConflictReason}: no `floor_sync_conflicts` row carries it, so
 * the S7 queue never lists it and migration 0022's CHECK does not admit it.
 * The wire's `conflict_reason` is open text, so the value reaches devices
 * without a contract change. The floor outbox reads it as futile and parks the
 * item (apps/web/src/areas/floor-pwa/lib/floor-outbox.ts).
 */
export const CLIENT_EVENT_ID_REUSE = "client_event_id_reuse" as const;

// --- the eleven §5.1 payloads ----------------------------------------------

/** `op_started` — `machine_id` required only when the op's work centre has
 * more than one active machine; in P2 it is ALWAYS omitted (D-073: module
 * 08's `equipment` does not exist, and the work centre is the machine grain).
 * Crew semantics per §3.2: op_started on an already-in_progress op is a
 * WORKER JOIN (opens a labor entry, changes no state), never
 * invalid_transition. */
export const OpStartedPayload = z
  .object({ machine_id: Uuid.optional() })
  .openapi("FloorOpStartedPayload");

/** `op_paused` — the pause may link a downtime reason. LIVE since 04-C-1
 * (the dormancy this doc used to state is over): a present
 * `downtime_reason_code` is resolved against the §2.6 catalog and OPENS a
 * `downtime_logs` row bound to the op — the pause interval IS the downtime —
 * auto-ended by the next `op_resumed`/`op_completed` on the op, or by an
 * explicit `downtime_ended` naming the pause tap (D-082). An unknown code is
 * an `unknown_reference` conflict. The pause/labor semantics (§3.2 — closes
 * ALL open entries on the op) apply regardless. */
export const OpPausedPayload = z
  .object({ downtime_reason_code: z.string().min(1).max(64).optional() })
  .openapi("FloorOpPausedPayload");

/** `op_resumed` — reopens a labor entry for the RESUMING worker only (§3.2);
 * other crew members re-join by tapping Start. */
export const OpResumedPayload = z
  .object({ downtime_reason_code: z.string().min(1).max(64).optional() })
  .openapi("FloorOpResumedPayload");

/** `op_completed` — `{}`: qty NEVER rides completion; it arrives via prior
 * `qty_reported` events, and the §3.2 guard demands at least one report
 * exists. The `is_test` pre-live mode (§3.10) is DORMANT in 04-B (§0.2
 * places it later); its fields ship because the §2.3 CHECK carve-out and this
 * contract are what let it arrive with no wire change. */
export const OpCompletedPayload = z
  .object({
    is_test: z.literal(true).optional(),
    /** Pack routing-step template ref — pre-live test mode only (§3.10). */
    routing_step_ref: z.string().min(1).max(128).optional(),
  })
  .openapi("FloorOpCompletedPayload");

/** `qty_reported` — reason required when `qty_scrap > 0` (service-enforced,
 * validated against Module 05 `waste_cause_codes.code`); a report reporting
 * nothing is not a report (§2.4's CHECK, mirrored here so the fat-finger
 * lands client-side too). */
export const QtyReportedPayload = z
  .object({
    qty_good: ProductionQty,
    qty_scrap: ProductionQty,
    scrap_reason_code: ScrapReasonCode.optional(),
  })
  .refine((v) => Number(v.qty_good) + Number(v.qty_scrap) > 0, {
    message: "qty_good + qty_scrap must be > 0 (§2.4)",
  })
  .openapi("FloorQtyReportedPayload");

/**
 * `material_issued` — the floor scan path AND its inverse. EXACTLY ONE of
 * `scan_code` (raw lot / stock-unit QR string, server-resolved —
 * `issue_method='scan'`) or `component_product_id` (the no-scan fallback,
 * recorded `issue_method='manual'`) per §5.1.
 *
 * `qty` is SIGNED, non-zero (amended 2026-08-05, sub-step 04-B-4 — D-081,
 * realizing D-076 pt 4): a NEGATIVE qty is the floor RETURN, riding this SAME
 * kind — one §5.1 row, server-validated. The server allocates a return
 * newest-first against the WO's unreturned issues of that component (narrowed
 * to the scanned unit and the wire UoM), refuses an over-return with
 * `MATERIAL_RETURN_EXCEEDS_ISSUED` semantics to the conflict queue, and costs
 * every returned slice at ITS original issue's captured unit cost (§9.14 —
 * never re-averaged). Widening from strictly-positive was additive on the
 * wire, exactly as the 04-B-2 comment predicted.
 */
export const MaterialIssuedPayload = z
  .object({
    scan_code: z.string().min(1).max(256).optional(),
    component_product_id: Uuid.optional(),
    qty: ProductionNonZeroSignedQty,
    uom: ProductionUom,
  })
  .refine((v) => (v.scan_code === undefined) !== (v.component_product_id === undefined), {
    message: "exactly one of scan_code / component_product_id (§5.1)",
  })
  .openapi("FloorMaterialIssuedPayload");

/** `downtime_started` — applied since 04-C-1.
 * `work_order_id`/`wo_operation_id` omitted for op-less downtime (§2.3
 * CHECK); `work_center_id` rides the envelope. */
export const DowntimeStartedPayload = z
  .object({
    machine_id: Uuid.optional(),
    downtime_reason_code: z.string().min(1).max(64),
    notes: z.string().max(2000).optional(),
  })
  .openapi("FloorDowntimeStartedPayload");

/** `downtime_ended` — references the OPENING event; the server matches its
 * open downtime_logs row (04-C). */
export const DowntimeEndedPayload = z
  .object({ started_client_event_id: Uuid })
  .openapi("FloorDowntimeEndedPayload");

/** `calibration_recorded` — APPLIED since 04-C-2 (2026-08-05).
 * `machine_claimed_qty` is what the vendor-locked display said; `measured_qty`
 * is scale/meter truth, and it is the ONLY number inventory, costing and
 * genealogy ever use (D-025). `output_stock_unit_scan_code` is the RAW barrel
 * scan, server-resolved through module 06 (§5.1's scan rule); when present the
 * applier corrects that unit's measure to `measured_qty` through module 06's
 * remeasure seam in the event's own transaction (D-065), posts the §3.6
 * `calibration_variance` cost entry (negative variance → cost, positive →
 * credit, §9.9 both directions), and an unresolvable/foreign scan answers
 * `unknown_reference` byte-identically (§9.14). */
/** The floor half of the ONE capture shape (D-084): field-for-field identical
 * to e34's office `calibration` object, because they carry the same physical
 * fact. The two OpenAPI component names differ; the fields cannot. */
export const CalibrationRecordedPayload = z
  .object(CalibrationCaptureShape)
  .openapi("FloorCalibrationRecordedPayload");

/**
 * `serial_lot_captured` — §3.3 step 8, LIVE 2026-08-06 (04-C-3: the kind left
 * {@link FLOOR_EVENT_KINDS_DEFERRED}, which is how an applier ships).
 *
 * At least one of serial_no / scan_code (§2.14's at-least-one CHECK, mirrored
 * here so a device learns the rule from the contract instead of from a
 * conflict row). `scan_code` is the RAW scan string per §5.1 — the server
 * resolves it to a module-06 lot or stock unit and answers `unknown_reference`
 * when it cannot, byte-identically for foreign and nonexistent (§9.14).
 *
 * `direction` is the whole point of the row: `consumed` says this lot went
 * INTO the run, `produced` says it came OUT of it. Only `produced` satisfies
 * the §3.2 `requires_serial_capture` guard, and only `produced` feeds §2.18's
 * measured-beats-ratio override.
 */
export const SerialLotCapturedPayload = z
  .object({
    direction: z.enum(["consumed", "produced"]),
    serial_no: z.string().min(1).max(128).optional(),
    scan_code: z.string().min(1).max(256).optional(),
    qty: ProductionPositiveQty,
    uom: ProductionUom,
  })
  .refine((v) => v.serial_no !== undefined || v.scan_code !== undefined, {
    message: "at least one of serial_no / scan_code (§5.1)",
  })
  .openapi("FloorSerialLotCapturedPayload");

/**
 * `defect_handoff_flagged` — §3.3 step 10, LIVE 2026-08-07 (04-C-5: the kind
 * left {@link FLOOR_EVENT_KINDS_DEFERRED}, which is how an applier ships).
 *
 * Photos are uploaded FIRST (`POST /v1/files`, purpose `production_evidence`)
 * and their ids ride here; the server refuses an id that is not a STORED
 * evidence file of this tenant with `unknown_reference`, byte-identically for
 * foreign, still-uploading and never-existed (§9.14).
 *
 * `produced_wo_operation_id` pins the producing OP when the flagger knows which
 * step made it — and it is the ONLY attribution the wire may carry. The SHIFT
 * that gets blamed, and the worker when the grain identifies one, are resolved
 * server-side by walking the operation's §2.4 timeline backwards from the flag
 * (§3.3 step 10; §5.1's "server-resolved fields are NEVER on the wire"). That
 * is not tidiness: `produced_shift_instance_id` decides whose scoreboard takes
 * the hit, and a device that could name it would BE the gaming vector §2.11
 * exists to close. A named operation belonging to a different work order —
 * or to another tenant — answers `unknown_reference` for the same reason.
 */
export const DefectHandoffFlaggedPayload = z
  .object({
    qty_defective: ProductionPositiveQty,
    scrap_reason_code: ScrapReasonCode,
    photo_file_ids: z.array(Uuid).max(10),
    produced_wo_operation_id: Uuid.optional(),
    notes: z.string().max(2000).optional(),
  })
  .openapi("FloorDefectHandoffFlaggedPayload");

/**
 * The per-kind payload table — §5.1 as one lookup. The apply pipeline parses
 * `event.payload` against `floorEventPayloadSchemas[event.event_type]`; a
 * parse failure is a `rejection` conflict, never a 4xx (§5.1's last line).
 */
export const floorEventPayloadSchemas: Readonly<Record<FloorEventTypeValue, z.ZodType>> = {
  op_started: OpStartedPayload,
  op_paused: OpPausedPayload,
  op_resumed: OpResumedPayload,
  op_completed: OpCompletedPayload,
  qty_reported: QtyReportedPayload,
  material_issued: MaterialIssuedPayload,
  downtime_started: DowntimeStartedPayload,
  downtime_ended: DowntimeEndedPayload,
  calibration_recorded: CalibrationRecordedPayload,
  serial_lot_captured: SerialLotCapturedPayload,
  defect_handoff_flagged: DefectHandoffFlaggedPayload,
};

// --- the batch envelope (endpoint 13) ---------------------------------------

/**
 * One device-outbox event on the wire (§5 row 13's `events[]` item). The
 * envelope is validated STRICTLY (a malformed envelope is a client bug);
 * `payload` is typed loose here and validated PER-KIND inside the pipeline —
 * see the header note on validation layering.
 */
export const FloorBatchEvent = z
  .object({
    /** Device-minted uuid v7, stamped AT TAP, sent unchanged on every attempt
     * — `clientKey === client_event_id` (D-080). THE only dedupe key. */
    client_event_id: Uuid,
    event_type: FloorEventType,
    /** D-079 interim identity rule: MUST equal the session principal, else
     * the event conflicts `worker_not_authenticated` — the session, not the
     * body, is the identity, so piece-rate credit cannot be spoofed. */
    worker_id: Uuid,
    /** The pinned station from the `/q/wc/{uuid}` scan; for op-bound events
     * server-validated to equal the op's work centre (§2.3). */
    work_center_id: Uuid,
    /** Absent only for op-less `downtime_*` (and dormant §3.10 test) events. */
    wo_operation_id: Uuid.optional(),
    /** Device wall clock at TAP — preserved VERBATIM in the event row;
     * attribution uses the §9.5 clamp, never this raw and never receipt time. */
    occurred_at: Timestamp,
    payload: z.record(z.string(), z.unknown()),
  })
  .openapi("FloorBatchEvent");

export const FloorEventsBatchRequest = z
  .object({
    /** The registered device posting the batch (§2.16) — registry context and
     * telemetry, NEVER an auth factor (D-079). */
    device_id: Uuid,
    /** Strict FIFO from the device outbox. Transport is batch-of-1 in 04-B
     * (D-080); the envelope takes an array so true batching later is
     * transport-only, not a wire change. */
    events: z.array(FloorBatchEvent).min(1).max(200),
  })
  .openapi("FloorEventsBatchRequest");

// --- the per-event result union ---------------------------------------------

/**
 * Endpoint 13 answers HTTP 200 with a result PER EVENT (unless auth fails) —
 * `applied` (projections written), `deduplicated` (this client_event_id was
 * already applied; the original result stands, zero projection deltas), or
 * `conflicted` (persisted into `floor_sync_conflicts`, projections untouched
 * for rejections). The client interprets BEFORE deleting from its outbox
 * (D-080): a `conflicted` result must never settle as success.
 */
export const FloorEventResult = z
  .discriminatedUnion("status", [
    z.object({ client_event_id: Uuid, status: z.literal("applied") }),
    z.object({ client_event_id: Uuid, status: z.literal("deduplicated") }),
    z.object({
      client_event_id: Uuid,
      status: z.literal("conflicted"),
      /** The `floor_sync_conflicts.reason` value ({@link FloorConflictReason})
       * — typed as open text on the wire so a future reason is not a breaking
       * response-enum change; the persisted row's CHECK is the closed set. */
      conflict_reason: z.string(),
    }),
  ])
  .openapi("FloorEventResult");

export const FloorEventsBatchResponse = z
  .object({ results: z.array(FloorEventResult) })
  .openapi("FloorEventsBatchResponse");

// ===========================================================================
// The rest of the 04-B-2 floor surface — DTOs for §5 rows 10, 15, 25, 26, 31.
// They live in this file (not schemas.ts) so the import direction stays
// acyclic: schemas.ts never imports floor-events.ts.
// ===========================================================================

// --- §5 row 10: the WO event timeline ---------------------------------------

/** One `operation_events` row (§2.3) as the office timeline serves it. The
 * payload is the raw event payload — quantities as decimal strings, scan
 * codes as sent; no money ever rides an event, so nothing here is redacted. */
export const OperationEvent = z
  .object({
    id: Uuid,
    work_order_id: Uuid.nullable(),
    wo_operation_id: Uuid.nullable(),
    work_center_id: Uuid,
    event_type: FloorEventType,
    worker_id: Uuid,
    device_id: Uuid.nullable(),
    /** Server-resolved shift attribution (clamped occurred_at against the
     * facility calendar — §9.5/§9.6); null only where resolution is not
     * defined for the kind. */
    shift_instance_id: Uuid.nullable(),
    client_event_id: Uuid,
    /** The device wall clock, VERBATIM as the wire carried it (§9.5). */
    occurred_at: Timestamp,
    recorded_at: Timestamp,
    payload: z.record(z.string(), z.unknown()),
  })
  .openapi("OperationEvent");

export const OperationEventListResponse = z
  .object({ items: z.array(OperationEvent), next_cursor: z.string().nullable() })
  .openapi("OperationEventListResponse");

// --- §5 row 25: the conflict queue ------------------------------------------

export const FloorConflictKind = z.enum(["rejection", "review_flag"]);

export const FloorConflictStatus = z.enum([
  "open",
  "applied_with_edit",
  "discarded",
  "acknowledged",
]);

/** One `floor_sync_conflicts` row (§2.17). `work_order_id` /
 * `wo_operation_id` / `device_id` are null when the event's reference did not
 * resolve (incl. §9.14's hostile foreign uuids — the raw claim survives in
 * `event_payload`). */
export const FloorSyncConflict = z
  .object({
    id: Uuid,
    device_id: Uuid.nullable(),
    client_event_id: Uuid,
    worker_id: Uuid.nullable(),
    work_order_id: Uuid.nullable(),
    wo_operation_id: Uuid.nullable(),
    kind: FloorConflictKind,
    reason: FloorConflictReason,
    /** The FULL rejected/flagged event — floor truth preserved (§2.17). */
    event_payload: z.record(z.string(), z.unknown()),
    status: FloorConflictStatus,
    resolved_by: Uuid.nullable(),
    resolved_at: Timestamp.nullable(),
    resolution_note: z.string().nullable(),
    created_at: Timestamp,
  })
  .openapi("FloorSyncConflict");

export const FloorConflictListResponse = z
  .object({ items: z.array(FloorSyncConflict), next_cursor: z.string().nullable() })
  .openapi("FloorConflictListResponse");

/**
 * Resolve a conflict (§5 row 25). Legal actions per row `kind` (§2.17,
 * service-enforced + DB CHECK): `rejection` → apply_with_edit | discard;
 * `review_flag` → acknowledge | apply_with_edit. `apply_with_edit` composes a
 * NEW correction event through the same apply pipeline — the original event
 * is preserved and projections are never edited in place. `note` is required
 * for `discard` (service-enforced): discarding floor truth without a stated
 * reason is the silent overwrite §2.17 exists to prevent.
 */
export const FloorConflictResolveRequest = z
  .object({
    action: z.enum(["apply_with_edit", "discard", "acknowledge"]),
    /** The corrected §5.1 payload for `apply_with_edit`; validated against
     * the event kind's schema exactly as a device payload would be. */
    corrected_payload: z.record(z.string(), z.unknown()).optional(),
    note: z.string().max(2000).optional(),
  })
  .openapi("FloorConflictResolveRequest");

/**
 * D-299 owner ruling R1 — what a review flag's `apply_with_edit` correction
 * did NOT change when it landed on a COMPLETED work order.
 *
 * The correction is allowed after completion (owner ruling A) and restates the
 * operation's quantity, its scoreboard cell, its piece-rate entry and module
 * 05's ledger by a signed delta. It does not restate what the completion
 * receipted: `work_orders.qty_good`, each `wo_outputs.qty_produced` and the
 * finished-goods lot in module 06 keep the figure taken at completion. Stock
 * is reconciled with a module-06 stock adjustment, a person's decision; this
 * object is how the office knows to offer it. Both numbers ride here so a
 * client can say the difference in the reader's language without re-reading.
 */
export const FloorCorrectionOutputReceiptUnchanged = z
  .object({
    output_receipt_unchanged: z.literal(true),
    work_order_id: Uuid,
    wo_operation_id: Uuid,
    /** `work_orders.qty_good` — what the completion receipted. */
    receipted_qty_good: ProductionQty,
    /** The corrected operation's quantities AFTER this correction. */
    corrected_operation_qty_good: ProductionQty,
    corrected_operation_qty_scrap: ProductionQty,
    uom: ProductionUom,
    /** `wo_outputs` as the completion left them, one per declared output. */
    receipted_outputs: z.array(
      z.object({
        product_id: Uuid,
        kind: WoOutputKind,
        qty: ProductionQty,
        uom: ProductionUom,
      }),
    ),
  })
  .openapi("FloorCorrectionOutputReceiptUnchanged");

export const FloorConflictResponse = z
  .object({
    conflict: FloorSyncConflict,
    /** Non-null only when a review flag's correction landed on a COMPLETED
     * work order, on the step its completion receipted from — the last
     * completed step (D-299 owner ruling R1; round 4); null for every other
     * resolution — a discard, an acknowledge, a rejection re-applied, a
     * correction on a live order (whose completion will receipt the corrected
     * quantity), a correction to an EARLIER step of a completed order (which
     * the receipt never read). */
    correction_effect: FloorCorrectionOutputReceiptUnchanged.nullable(),
  })
  .openapi("FloorConflictResponse");

// --- §5 row 26: the device registry -----------------------------------------

export const FloorDevice = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    /** Printed on the device; immutable once issued (§2.16). */
    device_code: z.string(),
    name: z.string(),
    registered_by: Uuid,
    last_seen_at: Timestamp.nullable(),
    last_sync_at: Timestamp.nullable(),
    /** DEVICE-reported queue depth at last sync — a hint for the office
     * "stale devices" panel, never a server-derived truth. */
    pending_outbox_hint: z.number().int(),
    active: z.boolean(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("FloorDevice");

/** The fleet per tenant is small (a handful of tablets per facility), so the
 * list is envelope-without-cursor — the shift-definition shape. */
export const FloorDeviceListResponse = z
  .object({ items: z.array(FloorDevice) })
  .openapi("FloorDeviceListResponse");

export const FloorDeviceCreateRequest = z
  .object({
    device_code: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    facility_id: Uuid,
    active: z.boolean().optional(),
  })
  .openapi("FloorDeviceCreateRequest");

/** PATCH: `device_code` is printed on the device and immutable; `active:
 * false` is the retire action — there is no DELETE, because operation_events
 * rows reference the device forever. */
export const FloorDeviceUpdateRequest = z
  .object({
    name: z.string().min(1).max(120).optional(),
    active: z.boolean().optional(),
  })
  .openapi("FloorDeviceUpdateRequest");

export const FloorDeviceResponse = z.object({ device: FloorDevice }).openapi("FloorDeviceResponse");

// --- §5 row 31: manual labor adjustments ------------------------------------

/** One `labor_time_entries` row (§2.12). `minutes` is DB-generated from the
 * interval (null while the timer runs); `shift_instance_id` is resolved
 * server-side from `started_at` against the facility calendar. Rates never
 * appear here — a labor entry is time, and money lands in `wo_cost_entries`
 * behind `production.view_costs`. */
export const LaborTimeEntry = z
  .object({
    id: Uuid,
    wo_operation_id: Uuid,
    worker_id: Uuid,
    shift_instance_id: Uuid,
    started_at: Timestamp,
    ended_at: Timestamp.nullable(),
    minutes: z.number().int().nullable(),
    entry_kind: z.enum(["setup", "run", "rework"]),
    source: z.enum(["timer", "manual_adjust"]),
    adjusted_by: Uuid.nullable(),
  })
  .openapi("LaborTimeEntry");

/** §5 row 31 — a manual entry/correction: always a CLOSED interval
 * (`source='manual_adjust'`, `adjusted_by` = the caller), reason required and
 * audit-chained. The §3.6 labor cost entry posts in the same transaction. */
export const LaborAdjustmentRequest = z
  .object({
    worker_id: Uuid,
    started_at: Timestamp,
    ended_at: Timestamp,
    entry_kind: z.enum(["setup", "run", "rework"]),
    reason: z.string().min(1).max(2000),
  })
  .openapi("LaborAdjustmentRequest");

export const LaborTimeEntryResponse = z
  .object({ labor_time_entry: LaborTimeEntry })
  .openapi("LaborTimeEntryResponse");

// --- §5 row 15: the station snapshot (full offline bootstrap) ---------------

/** The shift window the station is in RIGHT NOW, resolved by the kernel
 * against the facility calendar — total by D-078 (the provisioned GENERAL is
 * the last resort), so this key is always present. `shift_instance_id` is
 * null until the window's instance materializes (first event, or the 5-minute
 * tick) — the definition-level attribution is already decided either way. */
export const StationShift = z
  .object({
    shift_instance_id: Uuid.nullable(),
    shift_definition_id: Uuid,
    code: ShiftCode,
    /** The §2.9 definition's display name, SAME rule as `FloorDowntimeReason`
     * and `FloorScrapReason` below (added 2026-08-29). `code` is a stable
     * identifier the tenant keys on and stays what it is — but S9's banner used
     * to print it, so a Russian operator read "DAY" while the tenant's own
     * `Дневная смена` sat in `shift_definitions.name_i18n` and never crossed
     * the wire. The name travels BESIDE the code; the client resolves the
     * active locale, then `en`, then the code. */
    name_i18n: ShiftLocaleName,
    /** Facility-local date the shift STARTS (§9.7). */
    shift_date: z.string().openapi({ format: "date" }),
    started_at: Timestamp,
    ended_at: Timestamp,
  })
  .openapi("StationShift");

/** §2.6's catalog shape, published with e15 in 04-B so the 04-C-1 arrival of
 * `downtime_reasons` was a data change, not a wire change — which is exactly
 * what happened (2026-08-05): the array now carries the tenant's ACTIVE
 * reasons and the S10 downtime button renders its grid from it. */
export const FloorDowntimeReason = z
  .object({
    id: Uuid,
    code: z.string(),
    name_i18n: ShiftLocaleName,
    kind: z.enum([
      "machine_breakdown",
      "changeover",
      "material_wait",
      "no_operator",
      "quality_hold",
      "planned_maintenance",
      "power",
      "other",
    ]),
    triggers_maintenance: z.boolean(),
  })
  .openapi("FloorDowntimeReason");

/** A Module 05 `waste_cause_codes` row as the scrap-reason grid needs it. */
export const FloorScrapReason = z
  .object({ code: ScrapReasonCode, name_i18n: ShiftLocaleName })
  .openapi("FloorScrapReason");

/**
 * An OPEN `downtime_logs` row (§2.7, `ended_at IS NULL`) at the snapshot's
 * station — §5 e15's "open downtime", live since 04-C-1. The S10 indicator
 * renders these, and `started_client_event_id` is what lets the device compose
 * the §5.1 `downtime_ended` payload (the wire references the OPENING tap, and
 * a tablet that did not open the row has no other way to learn that id). Null
 * for a row a non-floor surface opened without a §2.3 event.
 */
export const FloorOpenDowntime = z
  .object({
    downtime_log_id: Uuid,
    downtime_reason_id: Uuid,
    /** §2.6 catalog code — the label resolves client-side from
     * `downtime_reasons[]`, so the row survives a reason rename. */
    reason_code: z.string(),
    started_at: Timestamp,
    wo_operation_id: Uuid.nullable(),
    started_client_event_id: Uuid.nullable(),
  })
  .openapi("FloorOpenDowntime");

/** Expected material per queued operation (§5 row 15 `materials_expected[]`):
 * the pinned BOM's plan against what has been issued, quantities only — no
 * money ever reaches a floor session (§7). `wo_operation_id` null = a
 * WO-level line not bound to a step. */
export const StationMaterialExpectation = z
  .object({
    work_order_id: Uuid,
    wo_operation_id: Uuid.nullable(),
    component_product_id: Uuid,
    product_name: z.string(),
    qty_planned: ProductionQty,
    /** Net of returns (Σ material_issues.qty). */
    qty_issued: ProductionSignedQty,
    uom: ProductionUom,
    /** The floor's ENTRY precision for `uom` (added 2026-08-08, D-091 —
     * ADDITIVE): the catalogue's `uoms.decimal_precision` clamped to 3 (the
     * §2.4/§2.5 `numeric(14,3)` wire ceiling), same rule as
     * `StationQueueOperation.uom_decimal_precision` — the material stepper's
     * numpad offers exactly this many fraction digits. Unknown code ⇒ 3. */
    uom_decimal_precision: z.number().int().min(0).max(3),
  })
  .openapi("StationMaterialExpectation");

/**
 * A registered device of the station's facility, as the snapshot serves it so
 * a tablet can IDENTIFY ITSELF (amended 2026-08-05, 04-B-3 — see
 * `StationSnapshotResponse`). Registry facts only: the row is context and
 * telemetry, NEVER an auth factor (D-079).
 */
export const StationSnapshotDevice = z
  .object({
    id: Uuid,
    /** Printed on the device (§2.16) — what the operator matches against. */
    device_code: z.string(),
    name: z.string(),
  })
  .openapi("StationSnapshotDevice");

/**
 * §5 row 15 — everything a device needs to run the station offline: the queue
 * (D-072 order, with its delta cursor), the reason catalogs, the current
 * shift, the expected materials per op — and, amended 2026-08-05 (04-B-3),
 * the TWO identity facts without which no client can compose a legal e13
 * batch at all:
 *
 *  * `worker_id` — the SESSION principal's uuid. D-079 demands every event's
 *    `worker_id` equal the session principal, but no endpoint served the
 *    caller its own principal id (`/v1/auth/tenants` returns memberships, not
 *    a subject; there is no `/v1/auth/me`) — so every honest tap would have
 *    landed `worker_not_authenticated`. The snapshot echoes the id the batch
 *    will be judged against, and the device stamps it into each tap. Cached
 *    with the snapshot, it survives the dead zone; the sign-out purge drops
 *    it with the rest of the read cache, so a hand-over cannot leak it.
 *  * `devices[]` — the ACTIVE registered devices of the station's facility.
 *    e13 answers 404 `FLOOR_DEVICE_NOT_FOUND` for an unregistered
 *    `device_id`, and the registry list (e26) is fenced to
 *    `production.floor.manage`, which floor_worker does not hold — so a
 *    tablet had no permitted path to learn the id S8 registered for it. The
 *    operator picks the row matching the code printed on the device (exactly
 *    one active device auto-picks).
 *
 * `downtime_reasons` was a STATED DEGRADATION in 04-B (empty until the §2.6
 * catalog existed). RESOLVED 2026-08-05 (04-C-1, D-071's mirror rule kept):
 * the array carries the tenant's ACTIVE reasons, and `open_downtime[]` — §5
 * e15's "open downtime", additive on the wire — carries the station's open
 * §2.7 rows so the S10 indicator and the End action survive a dead zone.
 */
export const StationSnapshotResponse = z
  .object({
    queue: StationQueueResponse,
    /** The tenant's ACTIVE §2.6 reasons — the S10 downtime grid (04-C-1). */
    downtime_reasons: z.array(FloorDowntimeReason),
    /** OPEN §2.7 rows at THIS station (04-C-1) — the open-downtime indicator
     * + the End action's `started_client_event_id` source. */
    open_downtime: z.array(FloorOpenDowntime),
    /** Module 05 `waste_cause_codes` — the scrap-reason grid (§3.3 step 6). */
    scrap_reasons: z.array(FloorScrapReason),
    shift: StationShift,
    materials_expected: z.array(StationMaterialExpectation),
    /** The session principal — the id every event's `worker_id` must equal
     * (D-079). See the schema doc above. */
    worker_id: Uuid,
    /** Active registered devices of the station's facility (§2.16) — the
     * tablet's only permitted way to learn its own registered id. */
    devices: z.array(StationSnapshotDevice),
  })
  .openapi("StationSnapshotResponse");
